import React, { useState, useEffect, useContext, useMemo } from 'react';
import AppContext from '../contexts/AppContext';
import InputWithCounter from '../components/utils/InputWithCounter';
import ValidatedPhoneInput, { isValidPhoneValue } from '../components/utils/ValidatedPhoneInput';
import FloatingFooter from '../components/utils/FloatingFooter';
import ConfirmModal from '../components/utils/ConfirmModal';
import InformModal from '../components/utils/InformModal';

import { FaPhone, FaBook, FaInstagram } from 'react-icons/fa';
import { MdEmail, MdOutlineWhatsapp, MdFacebook } from 'react-icons/md';
import { CgWebsite } from 'react-icons/cg';
import { FaSquareXTwitter, FaWeixin } from 'react-icons/fa6';

const SettingContact = () => {
  const { token, selectedRole, userInfo } = useContext(AppContext);

  const [edited, setEdited] = useState(null);
  const [initialEdited, setInitialEdited] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [informOpen, setInformOpen] = useState(false);
  const [informMsg, setInformMsg] = useState('');
  const [informType, setInformType] = useState('inform');

  useEffect(() => {
    if (!userInfo || !userInfo.roleInfo || !selectedRole?.id) return;
    const list = userInfo.roleInfo[selectedRole.type] || [];
    const matched = list.find(r => r.id === selectedRole.id);
    const contact = matched ? matched.contact : null;
    if (!contact) return setEdited(null);

    const ensureObj = v => (v && typeof v === 'object' ? v : {});
    const nextEdited = {
      phone: ensureObj(contact.phone),
      email: ensureObj(contact.email),
      whatsapp: ensureObj(contact.whatsapp),
      wechat: ensureObj(contact.wechat),
      website: ensureObj(contact.website),
      serviceDirectory: ensureObj(contact.serviceDirectory),
      facebook: ensureObj(contact.facebook),
      instagram: ensureObj(contact.instagram),
      X: ensureObj(contact.X),
    };

    setEdited(nextEdited);
    setInitialEdited(nextEdited);
  }, [userInfo, selectedRole]);

  const hasChanges = useMemo(
    () => JSON.stringify(edited) !== JSON.stringify(initialEdited),
    [edited, initialEdited]
  );

  const change = (field, value) => {
    const [key, prop] = field.split('_');
    setEdited(prev => ({
      ...prev,
      [key]: { ...(prev?.[key] || {}), [prop]: value },
    }));
  };

  const save = async () => {
    const body = { contact: edited };
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/private/venue/${selectedRole.id}/property/update`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        }
      );
      if (resp.ok) return { ok: true };
      const data = await resp.json().catch(() => ({}));
      return { ok: false, message: data.error || resp.statusText };
    } catch (e) {
      return { ok: false, message: 'Network error' };
    }
  };

  const onConfirmSave = async () => {
    const { ok, message } = await save();
    setConfirmOpen(false);
    if (ok) {
      window.location.reload();
    } else {
      setInformMsg(message);
      setInformType('error');
      setInformOpen(true);
    }
  };

  const handleSaveClick = () => {
    const invalidFields = [];
    if (!isValidPhoneValue(edited?.phone?.value)) invalidFields.push('Phone Number');
    if (!isValidPhoneValue(edited?.whatsapp?.value)) invalidFields.push('WhatsApp');

    if (invalidFields.length) {
      setInformMsg(`${invalidFields.join(' and ')} must be a valid number with country code before saving.`);
      setInformType('error');
      setInformOpen(true);
      return;
    }

    setConfirmOpen(true);
  };

  const getNameMaxLength = () => 32;

  const getValueMaxLength = (key, isPhone) => {
    if (isPhone) return 100;
    if (key === 'email') return 100;
    return 255;
  };

  const block = (icon, label, key, isPhone = false) => {
    const desc = edited?.[key]?.description ?? '';
    const hasDescription = desc !== '';

    return (
      <section className="rounded-[22px] border border-[#e6ebf8] bg-[linear-gradient(180deg,#ffffff_0%,#fbfcff_100%)] px-5 py-3.5 shadow-[0_10px_24px_rgba(15,23,42,0.035)] transition-all duration-200 hover:-translate-y-[1px] hover:border-[#d4ddf8] hover:shadow-[0_16px_30px_rgba(30,52,120,0.05)]">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#eef2ff] text-[#4663ff]">
              {icon}
            </div>
            <div>
              <h3 className="text-[1.02rem] font-semibold tracking-[-0.02em] text-[#1f2d4d]">{label}</h3>
            </div>
          </div>

          <label className="flex cursor-pointer items-center gap-2 rounded-full border border-[#d9e2f6] bg-[#f8faff] px-3 py-1.5 text-[0.78rem] font-medium text-[#53678f] transition-all duration-200 hover:-translate-y-[1px] hover:border-[#c8d3fb] hover:bg-[#eef2ff] hover:text-[#4154f5]">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-[#c9d5f3] text-[#4663ff] accent-[#4663ff]"
              checked={hasDescription}
              onChange={e => change(`${key}_description`, e.target.checked ? ' ' : '')}
            />
            Enable description
          </label>
        </div>

        <div className="space-y-2.5">
          <div>
            <div className="mb-1 flex items-center justify-between gap-3">
              <label className="text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#7f90b3]">
                Display Name
              </label>
            </div>
              <InputWithCounter
                compact
                flush
                type="text"
                value={edited?.[key]?.name || ''}
              maxLength={getNameMaxLength()}
              onChange={e => change(`${key}_name`, e.target.value)}
              placeholder={label}
            />
          </div>

          {hasDescription && (
            <div>
              <div className="mb-1 flex items-center justify-between gap-3">
                <label className="text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#7f90b3]">
                  Description
                </label>
              </div>
                <InputWithCounter
                  compact
                  flush
                  type="text"
                  value={edited?.[key]?.description || ''}
                maxLength={64}
                onChange={e => change(`${key}_description`, e.target.value)}
                placeholder="Enter description"
              />
            </div>
          )}

          <div>
            <div className="mb-1 flex items-center justify-between gap-3">
              <label className="text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#7f90b3]">
                Value
              </label>
            </div>
              {isPhone ? (
                <ValidatedPhoneInput
                  flush
                  value={edited?.[key]?.value || ''}
                  onChange={val => change(`${key}_value`, val)}
                />
            ) : (
                <InputWithCounter
                  compact
                  flush
                  type="text"
                  value={edited?.[key]?.value || ''}
                maxLength={getValueMaxLength(key, isPhone)}
                onChange={e => change(`${key}_value`, e.target.value)}
                placeholder="Enter value"
              />
            )}
          </div>
        </div>
      </section>
    );
  };

  if (!edited) return null;

  return (
    <div className="w-[90%] max-w-7xl mx-auto mb-20">
      <div className="mt-4 space-y-3 rounded-[28px] border border-[#e2e7f6] bg-white p-5 shadow-[0_18px_42px_rgba(15,23,42,0.05)]">
        {block(<FaPhone className="h-5 w-5 text-current" />, 'Phone Number', 'phone', true)}
        {block(<MdEmail className="h-5 w-5 text-current" />, 'Email Address', 'email')}
        {block(<MdOutlineWhatsapp className="h-5 w-5 text-current" />, 'WhatsApp', 'whatsapp', true)}
        {block(<FaWeixin className="h-5 w-5 text-current" />, 'WeChat', 'wechat')}
        {block(<CgWebsite className="h-5 w-5 text-current" />, 'Website', 'website')}
        {block(<FaBook className="h-5 w-5 text-current" />, 'E-Book/ Printable Version', 'serviceDirectory')}
        {block(<MdFacebook className="h-5 w-5 text-current" />, 'Facebook', 'facebook')}
        {block(<FaInstagram className="h-5 w-5 text-current" />, 'Instagram', 'instagram')}
        {block(<FaSquareXTwitter className="h-5 w-5 text-current" />, 'X (Twitter)', 'X')}
      </div>

      <FloatingFooter hasChanges={hasChanges} onSave={handleSaveClick} />

      <ConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={onConfirmSave}
        header="Confirm Save"
        content="Are you sure you want to save your profile?"
      />

      <InformModal
        isOpen={informOpen}
        onClose={() => setInformOpen(false)}
        content={informMsg}
        type={informType}
      />
    </div>
  );
};

export default SettingContact;
